import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bold, Italic, Link2, Image as ImageIcon, Heading2, List, Video } from "lucide-react";
import api, { apiErr } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function RichTextEditor({ value, onChange, testid = "rte", placeholder = "Write the story…" }) {
  const ref = useRef(null);
  const [imgBusy, setImgBusy] = useState(false);
  const [vidBusy, setVidBusy] = useState(false);

  // Seed the editor once (and when a different article is loaded in).
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => onChange(ref.current?.innerHTML || "");
  const exec = (cmd, arg) => { ref.current?.focus(); document.execCommand(cmd, false, arg); emit(); };
  const insertHTML = (html) => { ref.current?.focus(); document.execCommand("insertHTML", false, html); emit(); };
  const addLink = () => { const url = window.prompt("Link URL (https://…)"); if (url) exec("createLink", url); };

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return { url: `${BACKEND}${data.url}`, compressed: data.compressed };
  };
  const onImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return; setImgBusy(true);
    try { const { url, compressed } = await upload(file); insertHTML(`<p><img src="${url}" alt="" style="max-width:100%;" /></p><p><br/></p>`); toast.success(compressed ? "Image added (optimized)." : "Image added."); }
    catch (err) { toast.error(`Upload failed: ${apiErr(err)}`); }
    setImgBusy(false); e.target.value = "";
  };
  const onVideo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return; setVidBusy(true);
    try { const { url } = await upload(file); insertHTML(`<p><video controls playsinline src="${url}" style="max-width:100%;"></video></p><p><br/></p>`); toast.success("Video added."); }
    catch (err) { toast.error(`Upload failed: ${apiErr(err)}`); }
    setVidBusy(false); e.target.value = "";
  };

  const TB = ({ onClick, children, tid, title }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} data-testid={tid} title={title} aria-label={title}
      className="flex items-center justify-center w-8 h-8 text-gray-600 hover:bg-white hover:text-pti-red border border-transparent hover:border-gray-300 transition-colors">{children}</button>
  );

  return (
    <div data-testid={`${testid}-wrap`}>
      <div className="flex flex-wrap items-center gap-0.5 border border-gray-300 border-b-0 p-1 bg-gray-50" role="toolbar" aria-label="Formatting" data-testid={`${testid}-toolbar`}>
        <TB onClick={() => exec("formatBlock", "H2")} tid={`${testid}-h2`} title="Heading"><Heading2 size={16} /></TB>
        <TB onClick={() => exec("bold")} tid={`${testid}-bold`} title="Bold"><Bold size={16} /></TB>
        <TB onClick={() => exec("italic")} tid={`${testid}-italic`} title="Italic"><Italic size={16} /></TB>
        <TB onClick={() => exec("insertUnorderedList")} tid={`${testid}-list`} title="Bullet list"><List size={16} /></TB>
        <TB onClick={addLink} tid={`${testid}-link`} title="Insert link"><Link2 size={16} /></TB>
        <label data-testid={`${testid}-image`} title="Insert image" aria-label="Insert image"
          className="flex items-center justify-center w-8 h-8 text-gray-600 hover:bg-white hover:text-pti-red border border-transparent hover:border-gray-300 cursor-pointer transition-colors">
          {imgBusy ? <span className="text-[10px] font-bold">…</span> : <ImageIcon size={16} />}
          <input type="file" accept="image/*" onChange={onImage} className="hidden" />
        </label>
        <label data-testid={`${testid}-video`} title="Insert video (mp4, webm, mov)" aria-label="Insert video"
          className="flex items-center justify-center w-8 h-8 text-gray-600 hover:bg-white hover:text-pti-red border border-transparent hover:border-gray-300 cursor-pointer transition-colors">
          {vidBusy ? <span className="text-[10px] font-bold">…</span> : <Video size={16} />}
          <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={onVideo} className="hidden" />
        </label>
        <span className="ml-auto pr-1 text-[10px] text-gray-400">Live formatting</span>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-testid={testid}
        data-placeholder={placeholder}
        className="rte-editable prose prose-sm max-w-none w-full min-h-[220px] border border-gray-300 px-3 py-2 text-sm outline-none focus:border-pti-red overflow-y-auto"
      />
    </div>
  );
}
